import {useEffect, useState} from 'react';

const BASE = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';

function authHeaders() {
    const token = localStorage.getItem('access_token');
    return token ? {'Authorization': `Bearer ${token}`} : {};
}

export const DEFAULT_GROUPED_MENTIONS_PAGE_SIZE = 50;

/**
 * Fetches paginated grouped NER mention surfaces from
 * GET /api/documents/entity-mentions/grouped.
 *
 * One row per distinct (mention_text, entity_type) pair, sorted by total
 * occurrence count descending.
 *
 * @param {Object}  options
 * @param {string}  [options.search]       - Substring match on mention_text or canonical_name
 * @param {string}  [options.entityType]   - Entity type filter
 * @param {string}  [options.docSourceType]
 * @param {boolean} [options.inDataItems]
 * @param {boolean} [options.inCorpus]
 * @param {number}  [options.page]         - 0-based page index
 * @param {number}  [options.pageSize]
 * @returns {{
 *   groups: Array<{
 *     mentionText: string,
 *     entityType: string,
 *     totalCount: number,
 *     distinctDocCount: number,
 *     distinctEntityCount: number,
 *     topEntityId: number,
 *     topEntityRef: string,
 *     topCanonicalName: string,
 *   }>,
 *   loading: boolean,
 *   error: Error|null,
 *   totalElements: number,
 *   totalPages: number,
 * }}
 */
export function useGroupedMentions({
    search = '',
    entityType = '',
    docSourceType = '',
    inDataItems = false,
    inCorpus = false,
    minNerScore,
    page = 0,
    pageSize = DEFAULT_GROUPED_MENTIONS_PAGE_SIZE,
} = {}) {
    const [groups, setGroups]               = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages]       = useState(0);
    const [loading, setLoading]             = useState(true);
    const [error, setError]                 = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({page: String(page), size: String(pageSize)});
        if (search)        params.set('search', search);
        if (entityType)    params.set('type', entityType);
        if (docSourceType) params.set('docSourceType', docSourceType);
        if (inDataItems)   params.set('inDataItems', 'true');
        if (inCorpus)      params.set('inCorpus', 'true');
        // Omitting minNerScore lets the server apply its configured default (display threshold).
        if (minNerScore != null) params.set('minNerScore', String(minNerScore));

        fetch(`${BASE}/api/documents/entity-mentions/grouped?${params}`, {headers: authHeaders()})
            .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
            .then(data => {
                if (cancelled) return;
                const rows = (data.content || []).map(r => ({
                    mentionText:          r.mentionText          ?? r.mention_text          ?? '',
                    entityType:           r.entityType           ?? r.entity_type           ?? '',
                    totalCount:           Number(r.totalCount    ?? r.total_count)           || 0,
                    distinctDocCount:     Number(r.distinctDocCount ?? r.distinct_doc_count) || 0,
                    distinctEntityCount:  Number(r.distinctEntityCount ?? r.distinct_entity_count) || 0,
                    topEntityId:          r.topEntityId          ?? r.top_entity_id          ?? null,
                    topEntityRef:         r.topEntityRef         ?? r.top_entity_ref         ?? null,
                    topCanonicalName:     r.topCanonicalName     ?? r.top_canonical_name     ?? '',
                    manuallyLinked:       !!(r.manuallyLinked    ?? r.manually_linked),
                }));
                setGroups(rows);
                setTotalElements(data.totalElements ?? 0);
                setTotalPages(data.totalPages ?? 1);
            })
            .catch(err => {
                if (!cancelled) setError(err);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [search, entityType, docSourceType, inDataItems, inCorpus, minNerScore, page, pageSize]);

    return {groups, loading, error, totalElements, totalPages};
}
